/**
 * RescheduleDialog Component
 * 
 * Lightweight dialog for rescheduling individual posts or threads.
 * Reuses SchedulingWidget for consistent scheduling UI and conflict detection.
 */

import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Chip,
    Divider,
    Alert,
    IconButton,
    Paper,
    Stack,
    CircularProgress
} from '@mui/material';
import {
    Schedule as ScheduleIcon,
    Close as CloseIcon,
    AccessTime as TimeIcon,
    CheckCircle as CheckCircleIcon,
    Twitter as TwitterIcon,
    Facebook as FacebookIcon,
    Instagram as InstagramIcon,
    LinkedIn as LinkedInIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import SchedulingWidget from './compose/SchedulingWidget';
import { 
    getTimezoneDisplayInfo, 
    validateScheduleDate, 
    formatScheduledDate,
    getUserTimezone 
} from '../utils/schedulingHelpers';
import { useAuth } from '../providers/AuthProvider';
import { useTracking } from '../hooks/useTracking';
import { useStatus } from '../providers/MsgStatusProvider';
import { EnhancedPlatformEvents } from '../services/analytics/events';

// Extend dayjs
dayjs.extend(utc);
dayjs.extend(timezone);

// Styled components
const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        borderRadius: theme.spacing(2),
        minWidth: 480,
        maxWidth: 640,
        [theme.breakpoints.down('sm')]: {
            minWidth: '100%',
            margin: theme.spacing(1),
        }
    }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
    background: `linear-gradient(135deg, ${theme.palette.primary.main}10 0%, ${theme.palette.primary.main}05 100%)`,
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(3),
}));

const PostSummaryCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
}));

const TimeComparisonBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        gap: theme.spacing(1),
        alignItems: 'stretch'
    }
}));

const getPlatformIcon = (platform) => {
    switch (platform?.toLowerCase()) {
        case 'twitter':
            return <TwitterIcon />;
        case 'facebook':
            return <FacebookIcon />;
        case 'instagram':
            return <InstagramIcon />;
        case 'linkedin':
            return <LinkedInIcon />;
        default:
            return <ScheduleIcon />;
    }
};

const getPlatformColor = (platform) => {
    switch (platform?.toLowerCase()) {
        case 'twitter':
            return '#1DA1F2';
        case 'facebook':
            return '#4267B2';
        case 'instagram':
            return '#E4405F';
        case 'linkedin':
            return '#0077B5';
        default:
            return '#757575';
    }
};

const RescheduleDialog = ({ 
    open, 
    onClose, 
    post,
    onSuccess = null
}) => {
    const { user, token } = useAuth();
    const { trackEvent, trackWithError, trackWithSuccess } = useTracking();
    const { setStatusMessage, setIsAlertOpen, setStatusType } = useStatus();
    
    // State
    const [newScheduleTime, setNewScheduleTime] = useState('');
    const [isRescheduling, setIsRescheduling] = useState(false);
    const [rescheduleSuccess, setRescheduleSuccess] = useState(false);
    const [rescheduleResult, setRescheduleResult] = useState(null);
    const [timezoneInfo, setTimezoneInfo] = useState(null);
    const [scheduleValidation, setScheduleValidation] = useState(null);

    // Initialize timezone info
    useEffect(() => {
        if (user?.[0]?.userprofile) {
            const tzInfo = getTimezoneDisplayInfo(user[0].userprofile);
            setTimezoneInfo(tzInfo);
        }
    }, [user]);

    // Validate schedule date when it changes
    useEffect(() => {
        if (newScheduleTime && user?.[0]?.userprofile) {
            const subscriptionTier = user[0].userprofile.subscription_tier || 'free';
            const validation = validateScheduleDate(newScheduleTime, subscriptionTier);
            setScheduleValidation(validation);
        } else {
            setScheduleValidation(null);
        }
    }, [newScheduleTime, user]);

    // Reset state when dialog opens/closes
    useEffect(() => {
        if (open) {
            setNewScheduleTime('');
            setIsRescheduling(false);
            setRescheduleSuccess(false);
            setRescheduleResult(null);
            setScheduleValidation(null);
        }
    }, [open]);

    // Return early if no post
    if (!post) {
        return null;
    }

    // Extract post information
    const platforms = post.platform_details?.map(pd => pd.platform) || [];
    const currentScheduleTime = post.scheduled_post_date;
    const postText = post.post_text || '';
    const isThread = post.is_thread;

    // Handle reschedule submission
    const handleReschedule = async () => {
        if (!newScheduleTime || !post.id) return;

        setIsRescheduling(true);

        try {
            trackEvent(EnhancedPlatformEvents.POST_RESCHEDULE_STARTED.name, {
                postId: post.id,
                oldScheduleTime: currentScheduleTime,
                newScheduleTime: newScheduleTime,
                isThread: isThread,
                platforms: platforms
            });

            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}post/posts/${post.id}/reschedule/`,
                {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Token ${token}`
                    },
                    body: JSON.stringify({
                        new_schedule_time: newScheduleTime
                    })
                }
            );

            const result = await response.json();

            if (response.ok && result.success) {
                setRescheduleSuccess(true);
                setRescheduleResult(result);

                // Track success
                trackWithSuccess(EnhancedPlatformEvents.POST_RESCHEDULE_SUCCESS, {
                    postId: post.id,
                    isThread: result.is_thread,
                    threadPostCount: result.thread_posts?.length || 1
                });

                // Call success callback if provided
                if (typeof onSuccess === 'function') {
                    onSuccess(result);
                }

                // Show success message
                const userTz = getUserTimezone(user[0].userprofile);
                const formattedDate = formatScheduledDate(newScheduleTime, userTz);
                const message = result.is_thread 
                    ? `Thread with ${result.thread_posts?.length || 1} posts rescheduled to ${formattedDate}`
                    : `Post rescheduled to ${formattedDate}`;
                
                setStatusMessage(message);
                setStatusType('success');
                setIsAlertOpen(true);

            } else {
                throw new Error(result.error || 'Failed to reschedule post');
            }

        } catch (error) {
            console.error('Error rescheduling post:', error);
            
            trackWithError(EnhancedPlatformEvents.POST_RESCHEDULE_FAILURE, error);
            
            setStatusMessage(error.message || 'Failed to reschedule post');
            setStatusType('error');
            setIsAlertOpen(true);
        } finally {
            setIsRescheduling(false);
        }
    };

    // Handle dialog close
    const handleClose = () => {
        if (!isRescheduling) {
            onClose();
        }
    };

    // Format current schedule time
    const formatCurrentTime = () => {
        if (!currentScheduleTime) return 'Not scheduled';
        const userTz = getUserTimezone(user?.[0]?.userprofile);
        return formatScheduledDate(currentScheduleTime, userTz);
    };

    // Check if we can reschedule
    const canReschedule = newScheduleTime && 
                         scheduleValidation?.isValid !== false && 
                         !isRescheduling &&
                         !rescheduleSuccess;

    return (
        <StyledDialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
        >
            <StyledDialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <ScheduleIcon color="primary" sx={{ fontSize: 28 }} />
                        <Box>
                            <Typography variant="h6" fontWeight="600">
                                Reschedule {isThread ? 'Thread' : 'Post'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {isThread ? 'Entire thread will be rescheduled together' : 'Change the scheduled time'}
                            </Typography>
                        </Box>
                    </Box>
                    
                    <IconButton 
                        onClick={handleClose} 
                        disabled={isRescheduling}
                        sx={{ color: 'text.secondary' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </StyledDialogTitle>

            <DialogContent sx={{ p: 3 }}>
                {rescheduleSuccess ? (
                    // Success State
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                        <CheckCircleIcon color="success" sx={{ fontSize: 48, mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                            {rescheduleResult?.is_thread ? 'Thread Rescheduled!' : 'Post Rescheduled!'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                            {rescheduleResult?.message}
                        </Typography>
                        
                        {rescheduleResult?.is_thread && rescheduleResult?.thread_posts && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Updated Thread Posts:
                                </Typography>
                                <Stack spacing={1}>
                                    {rescheduleResult.thread_posts.map((threadPost, index) => (
                                        <Box key={threadPost.id} sx={{ 
                                            p: 1, 
                                            bgcolor: 'background.default',
                                            borderRadius: 1,
                                            fontSize: '0.875rem'
                                        }}>
                                            Post {threadPost.sequence_order}: {formatScheduledDate(threadPost.new_schedule_time, getUserTimezone(user?.[0]?.userprofile))}
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        )}
                    </Box>
                ) : (
                    // Reschedule Form
                    <>
                        {/* Post Summary */}
                        <PostSummaryCard elevation={0}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        {postText.substring(0, 100)}{postText.length > 100 ? '...' : ''}
                                    </Typography>
                                    
                                    {/* Platform chips */}
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        {platforms.map((platform, index) => (
                                            <Chip
                                                key={index}
                                                icon={getPlatformIcon(platform)}
                                                label={platform}
                                                size="small"
                                                sx={{
                                                    backgroundColor: getPlatformColor(platform) + '15',
                                                    color: getPlatformColor(platform),
                                                    fontWeight: 'medium'
                                                }}
                                            />
                                        ))}
                                        {isThread && (
                                            <Chip
                                                label="Thread"
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        </PostSummaryCard>

                        {/* Current vs New Time Comparison */}
                        <TimeComparisonBox>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    Current Schedule
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <TimeIcon fontSize="small" color="action" />
                                    <Typography variant="body2" fontWeight="medium">
                                        {formatCurrentTime()}
                                    </Typography>
                                </Box>
                            </Box>
                            
                            {newScheduleTime && (
                                <>
                                    <Typography variant="h6" color="text.secondary" sx={{ mx: 2 }}>
                                        →
                                    </Typography>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                            New Schedule
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <ScheduleIcon fontSize="small" color="primary" />
                                            <Typography variant="body2" fontWeight="medium" color="primary.main">
                                                {formatScheduledDate(newScheduleTime, getUserTimezone(user?.[0]?.userprofile))}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </>
                            )}
                        </TimeComparisonBox>

                        <Divider sx={{ my: 2 }} />

                        {/* Scheduling Widget */}
                        <SchedulingWidget
                            postDate={newScheduleTime}
                            onDateChange={setNewScheduleTime}
                            disabled={isRescheduling}
                            timezoneInfo={timezoneInfo}
                            scheduleValidation={scheduleValidation}
                            selectedPlatforms={platforms}
                            postContent={postText}
                            editPostId={post.id}
                            showQuickSchedule={true}
                            showConflictDetection={true}
                            isCompact={false}
                            label="New Schedule Date & Time"
                        />

                        {/* Thread Warning */}
                        {isThread && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                <Typography variant="body2">
                                    <strong>Thread Reschedule:</strong> All posts in this thread will be rescheduled together, 
                                    maintaining their relative timing.
                                </Typography>
                            </Alert>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                {rescheduleSuccess ? (
                    <Button
                        onClick={handleClose}
                        variant="contained"
                        startIcon={<CheckCircleIcon />}
                        size="large"
                        sx={{ px: 4 }}
                    >
                        Done
                    </Button>
                ) : (
                    <>
                        <Button
                            onClick={handleClose}
                            disabled={isRescheduling}
                            size="large"
                            sx={{ px: 3 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleReschedule}
                            variant="contained"
                            disabled={!canReschedule}
                            startIcon={isRescheduling ? <CircularProgress size={20} /> : <ScheduleIcon />}
                            size="large"
                            sx={{ px: 4 }}
                        >
                            {isRescheduling ? 'Rescheduling...' : 'Reschedule'}
                        </Button>
                    </>
                )}
            </DialogActions>
        </StyledDialog>
    );
};

export default RescheduleDialog; 